import React, { useContext } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { TableWrapper } from '../TableView/TableView.style';
import { useSavingCalculationContext } from '../Contexts/SavingCalculationProvider';
import { SettingsContext } from 'contexts/SettingsContext';

const AgGridTable = props => {
	const { tableData, setTableData } = props;
	const { isWaccEnabled } = useSavingCalculationContext();
	const { hideDecimals } = useContext(SettingsContext);

	const descriptionRenderer = params => {
		if (params.value) {
			const textWithLineBreaks = params.value?.split('##').join(' <br/> ');
			return `<div style="white-space: pre-line;">${textWithLineBreaks}</div>`;
		} else {
			return null;
		}
	};

	const customAggFunc = values => {
		const total = values.reduce((sum, value) => sum + value, 0);
		return total;
	};

	const cellRenderer = params => {
		if (params.value || params.value === 0) {
			let value = formatSpendWithDecimalAndSymbol(
				params.value,
				'$',
				hideDecimals,
				2,
				true
			).toLocaleUpperCase();
			return value;
		} else {
			return null;
		}
	};

	const columnDefs = [
		{
			headerName: 'Filter By',
			field: 'filterBy',
			rowGroup: true,
			hide: true,
			width: 180,
		},
		{ headerName: 'Savings Lever', field: 'savingsLever', width: 130 },
		{
			headerName: 'Description',
			field: 'description',
			cellRenderer: descriptionRenderer,
			width: 220,
		},
		{
			headerName: 'Savings Low',
			field: 'savingsLow',
			width: 60,
			aggFunc: customAggFunc,
			cellRenderer: cellRenderer,
			hide: isWaccEnabled ? true : false,
			cellStyle: { textAlign: 'right' },
		},
		{
			headerName: 'Savings High',
			field: 'savingsHigh',
			width: 61,
			aggFunc: customAggFunc,
			cellRenderer: cellRenderer,
			hide: isWaccEnabled ? true : false,
			cellStyle: { textAlign: 'right' },
		},
		{
			headerName: 'Savings Avg',
			field: 'savingsAvg',
			width: 60,
			aggFunc: customAggFunc,
			cellRenderer: cellRenderer,
			sortable: true,
			cellStyle: { textAlign: 'right' },
			// sort: 'desc',
		},
	];

	const defaultColDef = {
		sortable: false,
		filter: false,
	};

	const rowData = tableData.reduce((acc, row) => {
		if (!row.collapse) {
			row.savingsLever.forEach((lever, index) => {
				acc.push({
					filterBy: row.filterBy,
					savingsLever: lever,
					description: row.description[index],
					savingsLow: row.savingsLow[index],
					savingsHigh: row.savingsHigh[index],
					savingsAvg: row.savingsAvg[index],
				});
			});
		} else {
			acc.push({
				filterBy: row.filterBy,
				savingsLever: row.savingsLever[0],
				description: row.description[0],
				savingsLow: row.savingsLow[0],
				savingsHigh: row.savingsHigh[0],
				savingsAvg: row.savingsAvg[0],
			});
		}
		return acc;
	}, []);

	const pinnedBottomRowData = [
		{
			savingsLever: '',
			description: 'Total',
			savingsLow: tableData.reduce((sum, obj) => {
				return (
					sum + obj.savingsLow.reduce((innerSum, val) => innerSum + val, 0)
				);
			}, 0),

			savingsHigh: tableData.reduce((sum, obj) => {
				return (
					sum + obj.savingsHigh.reduce((innerSum, val) => innerSum + val, 0)
				);
			}, 0),

			savingsAvg: tableData.reduce((sum, obj) => {
				return (
					sum + obj.savingsAvg.reduce((innerSum, val) => innerSum + val, 0)
				);
			}, 0),
		},
	];

	const getRowHeight = params => {
		if (
			(params.node.childIndex === 1 || params.node.childIndex === 2) &&
			params.data
		) {
			return 60;
		}
		return 30;
	};

	const getRowStyle = params => {
		if (params.node.group) {
			return {
				backgroundColor: '#f5f7f7',
				// backgroundColor: '#dde4e6',
			};
		}
		return null;
	};

	const onCellClicked = event => {
		if (event.colDef.field === 'filterBy') {
			const groupName = event.value;
			const rowNode = event.node;
			const newCollapseState = !rowNode.expanded;

			rowNode.setExpanded(newCollapseState);

			const updatedData = tableData.map(
				row =>
					row.filterBy === groupName
						? { ...row, collapse: newCollapseState }
						: row
			);
			setTableData(updatedData);
		}
	};
	const autoGroupColumnDef = {
		cellRendererParams: {
			suppressCount: true,
		},
	};
	return (
		<TableWrapper>
			<div
				className="ag-theme-balham saving-calculation"
				style={{ height: 415, width: '100%', margin: '20px 0px 20px 0px' }}
			>
				<AgGridReact
					rowData={rowData}
					defaultColDef={defaultColDef}
					columnDefs={columnDefs}
					animateRows={false}
					groupDefaultExpanded={false}
					modules={AllModules}
					onGridReady={params => params.api.sizeColumnsToFit()}
					onCellClicked={onCellClicked}
					getRowHeight={getRowHeight}
					getRowStyle={getRowStyle}
					pagination
					paginationPageSize={10}
					pinnedBottomRowData={pinnedBottomRowData}
					autoGroupColumnDef={autoGroupColumnDef}
					suppressRowClickSelection={true}
					suppressCellSelection={true}
					suppressRowHoverHighlight={true}
					suppressCellFocus={true}
					suppressAggFuncInHeader={true}
				/>
			</div>
		</TableWrapper>
	);
};

export default AgGridTable;
